import React, {useCallback, useEffect, useRef, useState} from "react";
import {Spin, Table, Typography} from "antd";
import dayjs from "dayjs";
import PropTypes from "prop-types";

import {getDistanceUnit} from "../../../helpers/booking";
import {getCarTrips} from "../../../helpers/car";
import {messageType, showMessages} from "../../../helpers/message";
import {truncateString} from "../../../helpers/misc";
import {dateTimeFormat} from "../../../lib/time";

import "./CarTripLog.less";

/*
 * This component is a table that is populated with Trips
 * they can either be from hardware or manual trip log entries.
 */
const CarTripLog = ({carID}) => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);

    const [loading, setLoading] = useState(false);

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(10);

    const [hardwareTripLogs, setHardwareTripLogs] = useState([]);

    const fetchHardwareTripLogs = useCallback( async (page, perPage) => {
        setLoading(true);
        try {
            const abortSignal = abortSignalRef.current;
            let response = await getCarTrips(
                carID,
                {page, perPage},
                abortSignal
            );

            if (abortSignal.aborted === false && response.length !== 0) {
                setHardwareTripLogs(response.data);
                setTotalRows(response.meta.total);
            }

            setLoading(false);
        } catch (error) {
            showMessages(error.message, messageType.error);
            setLoading(false);
            if(error.status_code === 404) window.location.replace('/404');
        }
    }, [carID]);

    // onMount function.
    useEffect(() => {
        fetchHardwareTripLogs(1, 10);
    }, [fetchHardwareTripLogs]);


    // onUnmount cleanup function.
    useEffect(() => {
        const abortController = abortControllerRef.current;
        return function abort() {
            abortController.abort();
        }
    }, []);

    const onPageChange = (page, perPage) => {
        setPage(page);
        setPerPage(perPage);
        fetchHardwareTripLogs(page, perPage)
    };

    const paginationAttr = {
        current: page,
        pageSize: perPage,
        total: totalRows,
        onChange: onPageChange,
        showSizeChanger: true,
        simple: window.innerWidth <= 767,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        onShowSizeChange: onPageChange,
    };

    const columns = [
        {
            title: <Typography.Text strong>Driver</Typography.Text>,
            dataIndex: 'person',
            key: 'person',
            render: person => {
                return <span className="car-trip-log-driver-line-item long-text-wrap">{person || 'N/A'}</span>
            },
        },
        {
            title: <Typography.Text strong>Start Time</Typography.Text>,
            dataIndex: 'start_time',
            key: 'start_time',
            render: start_time => {
                return <span className="car-trip-log-start-time-line-item long-text-wrap">
                {start_time ? dayjs(start_time).format(dateTimeFormat) : 'N/A'}
            </span>
            },
        },
        {
            title: <Typography.Text strong>End Time</Typography.Text>,
            dataIndex: 'end_time',
            key: 'end_time',
            render: end_time => {
                return <span className="car-trip-log-end-time-line-item long-text-wrap">
                {end_time ? dayjs(end_time).format(dateTimeFormat) : 'N/A'}
            </span>
            },
        },
        {
            title: <Typography.Text strong>Distance ({getDistanceUnit()})</Typography.Text>,
            dataIndex: 'distance',
            key: 'distance',
            render: distance => {
                return <span className="car-trip-log-distance-line-item long-text-wrap">{distance}</span>
            },
        },
        {
            title: <Typography.Text strong>End Location</Typography.Text>,
            dataIndex: 'end_location',
            key: 'end_location',
            render: end_location => {
                return <span className="car-trip-end-location-line-item long-text-wrap">
                {/* If both coordinates are null, or both are 0.000... then display N/A */}
                    {((end_location[0] && end_location[1]) && (end_location[0] % 1.1 !== Math.abs(0) || end_location[1] % 1.1 !== Math.abs(0))) ?
                        <a style={{'textDecoration': 'underline'}} target="_empty"
                           href={`https://maps.google.com/?q=${end_location[0]},${end_location[1]}`}>
                            {truncateString(end_location[0], 6)} || {truncateString(end_location[1], 8)}
                        </a>
                        : 'N/A'
                    }
            </span>
            },
        },
    ];

    return (
        <Spin spinning={loading}>
            <Table
                rowKey="id"
                dataSource={hardwareTripLogs}
                columns={columns}
                bordered={false}
                className="responsive-table"
                title={() =>
                    <div>
                        <Typography.Text>Trip Log</Typography.Text>
                        <Typography.Text className="car-trip-log-description-text">
                            - This includes onboard and manual trips
                        </Typography.Text>
                    </div>
            }
                extra={"hi"}
                pagination={paginationAttr}
            />
        </Spin>
    )
}

CarTripLog.propTypes = {
    carID: PropTypes.number.isRequired
};

export default CarTripLog;
